<!--
  SearchBox.vue —— 全站头部搜索（联想搜索 / autocomplete）
  设计参考 Apple：磨砂下拉面板、SF 字体、柔和阴影、流畅动效。
  能力：
    · 输入即联想（防抖 220ms，丢弃过期响应）
    · 命中文字高亮、封面缩略图、作者副标题
    · 历史搜索（localStorage，最多 8 条，可单删/清空）
    · 热门搜索（取点击榜书名）
    · 键盘上下/回车/Esc 操作
    · 加载骨架 / 空结果 / 异常 兜底
    · 移动端全屏搜索面板（iOS Spotlight 风格）
-->
<template>
  <div class="rn-search" :class="{ 'rn-search--light': light && !sheet, 'rn-search--open': open, 'rn-search--sheet': sheetActive }" @click.self="onSelfTap">
    <!-- 搜索条（头部触发器，移动端展开为顶部输入框） -->
    <div class="rn-search__bar" @click="focusInput">
      <svg class="rn-search__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
        <path d="M16.5 16.5 21 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      </svg>
      <input
        ref="input"
        v-model="query"
        class="rn-search__input"
        type="text"
        :placeholder="placeholder"
        autocomplete="off"
        spellcheck="false"
        enterkeyhint="search"
        @focus="onFocus"
        @input="onInput"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="onEnter"
        @keydown.esc.prevent="close"
      />
      <button v-show="query" class="rn-search__clear" type="button" aria-label="Clear" @click.stop="clearQuery">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.18" />
          <path d="M8.5 8.5 15.5 15.5M15.5 8.5 8.5 15.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </button>
    </div>
    <button v-if="sheet" class="rn-search__cancel" type="button" @click="close">Cancel</button>

    <!-- 联想面板 -->
    <transition name="rn-pop" @after-leave="onPanelLeft">
      <div v-if="open" class="rn-search__panel" @mousedown.prevent @click.self="onSelfTap">
        <div class="rn-search__scroll" @click.self="onSelfTap">
          <!-- 输入中：联想结果 -->
          <template v-if="hasQuery">
            <!-- 加载骨架 -->
            <div v-if="loading" class="rn-skeleton">
              <div v-for="n in 4" :key="n" class="rn-skeleton__row">
                <span class="rn-skeleton__cover"></span>
                <span class="rn-skeleton__lines">
                  <span class="rn-skeleton__line rn-skeleton__line--w70"></span>
                  <span class="rn-skeleton__line rn-skeleton__line--w40"></span>
                </span>
              </div>
            </div>

            <!-- 结果列表 -->
            <template v-else-if="results.length">
              <div class="rn-section__title">Novels</div>
              <button
                v-for="(book, i) in results"
                :key="book.id"
                type="button"
                class="rn-result"
                :class="{ 'is-active': activeIndex === i }"
                @mouseenter="activeIndex = i"
                @click="goBook(book)"
              >
                <span class="rn-result__cover">
                  <img v-if="book.picUrl" :src="book.picUrl" alt="" loading="lazy" />
                </span>
                <span class="rn-result__meta">
                  <span class="rn-result__name">
                    <span v-for="(p, pi) in highlightParts(book.bookName)" :key="pi" :class="{ hit: p.hit }">{{ p.t }}</span>
                  </span>
                  <span class="rn-result__sub">
                    <span v-if="book.authorName" class="rn-result__author">{{ book.authorName | maskAuthor }}</span>
                    <span v-if="book.score" class="rn-result__score">
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 17.3 6.2 20.5l1.1-6.5L2.6 9.3l6.5-1L12 2.5l2.9 5.8 6.5 1-4.7 4.7 1.1 6.5z" fill="currentColor"/></svg>
                      {{ book.score }}
                    </span>
                  </span>
                </span>
                <svg class="rn-result__chev" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m9 6 6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </template>

            <!-- 空结果 -->
            <div v-else class="rn-empty">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="1.6" />
                <path d="M16.5 16.5 21 21" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
              </svg>
              <p>No matches for "{{ query.trim() }}"</p>
            </div>

            <!-- 查看全部 -->
            <button
              type="button"
              class="rn-searchall"
              :class="{ 'is-active': activeIndex === results.length }"
              @mouseenter="activeIndex = results.length"
              @click="fullSearch(query)"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
                <path d="M16.5 16.5 21 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
              <span>Search all results for <b>“{{ query.trim() }}”</b></span>
            </button>
          </template>

          <!-- 空闲态：历史 + 热门 -->
          <template v-else>
            <div v-if="history.length" class="rn-block">
              <div class="rn-section__title">
                Recent
                <button type="button" class="rn-section__action" @click="clearHistory">Clear</button>
              </div>
              <div class="rn-chips">
                <span
                  v-for="(term, i) in history"
                  :key="'h' + term"
                  class="rn-chip"
                  :class="{ 'is-active': activeIndex === i }"
                  @mouseenter="activeIndex = i"
                  @click="fullSearch(term)"
                >
                  <svg class="rn-chip__lead" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.6" />
                    <path d="M12 7.5V12l3 2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  <span class="rn-chip__text">{{ term }}</span>
                  <span class="rn-chip__del" @click.stop="removeHistory(term)" aria-label="Remove">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
                  </span>
                </span>
              </div>
            </div>

            <div v-if="trending.length" class="rn-block">
              <div class="rn-section__title">Trending</div>
              <button
                v-for="(term, j) in trending"
                :key="'t' + term"
                type="button"
                class="rn-trend"
                :class="{ 'is-active': activeIndex === history.length + j }"
                @mouseenter="activeIndex = history.length + j"
                @click="fullSearch(term)"
              >
                <span class="rn-trend__rank" :class="'rn-trend__rank--' + (j < 3 ? 'hot' : 'cool')">{{ j + 1 }}</span>
                <span class="rn-trend__text">{{ term }}</span>
                <svg v-if="j < 3" class="rn-trend__flame" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2c1.5 3.5-1.5 5-1.5 7.5A2.5 2.5 0 0 0 13 12c.8-.6 1-1.6 1-1.6 1.7 1.4 3 3.4 3 6a5 5 0 1 1-10 0c0-3 2-5 2.5-7 .3-1.2 1.5-2.4 2.5-3.4z" fill="currentColor"/>
                </svg>
              </button>
            </div>

            <div v-if="!history.length && !trending.length" class="rn-tip">
              Search novels by title or author
            </div>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { searchget } from "@/api/classify/index"
import { indexClickRank } from "@/api/index"

const HISTORY_KEY = "rn_search_history"
const HISTORY_MAX = 8

export default {
  name: "SearchBox",
  props: {
    // 头部浮于深色 hero 之上时传 true，切换为浅色（白字）样式
    light: { type: Boolean, default: false },
  },
  data() {
    return {
      query: "",
      open: false,
      loading: false,
      results: [],
      history: [],
      trending: [],
      activeIndex: -1,
      isMobile: false,
      leaving: false,       // 全屏面板离场动画期间保持全屏布局，避免塌回桌面下拉态
      debounceTimer: null,
      reqSeq: 0,            // 联想请求序号，丢弃过期响应
      trendingLoaded: false,
    }
  },
  computed: {
    placeholder() {
      return "Search"
    },
    hasQuery() {
      return this.query.trim().length > 0
    },
    // 移动端 + 打开 = 全屏面板
    sheet() {
      return this.open && this.isMobile
    },
    // 视觉上是否处于全屏态：包含离场动画期间，保证面板淡出而非塌回桌面下拉
    sheetActive() {
      return this.isMobile && (this.open || this.leaving)
    },
    // 键盘导航的扁平条目数量
    navCount() {
      if (this.hasQuery) return this.results.length + 1 // 结果 + “查看全部”
      return this.history.length + this.trending.length
    },
  },
  watch: {
    // 路由变化时收起面板
    $route() {
      this.close()
    },
    // 进入/退出移动端全屏面板（含离场动画）：解除/恢复祖先的 fixed 包含块
    sheetActive(val) {
      this.$nextTick(() => {
        if (val) this.unclipFixed()
        else this.reclipFixed()
      })
    },
  },
  methods: {
    /* ---------- 焦点 / 开合 ---------- */
    focusInput() {
      if (this.$refs.input) this.$refs.input.focus()
    },
    onFocus() {
      this.open = true
      this.lockScroll()
      if (!this.trendingLoaded) this.loadTrending()
      if (this.hasQuery && !this.results.length) this.runSuggest()
    },
    close() {
      // 全屏态收起时，先维持全屏布局直到离场动画结束，避免面板塌回桌面下拉态再消失
      if (this.isMobile && this.open) {
        this.leaving = true
        // 主路径由面板 transition 的 @after-leave 精确收尾；定时器仅作兜底（如禁用动画时）
        clearTimeout(this.leaveTimer)
        this.leaveTimer = setTimeout(this.onPanelLeft, 320)
      }
      this.open = false
      this.activeIndex = -1
      this.unlockScroll()
      if (this.$refs.input) this.$refs.input.blur()
    },

    /* ---------- 输入 / 联想 ---------- */
    onInput() {
      this.activeIndex = -1
      clearTimeout(this.debounceTimer)
      const q = this.query.trim()
      if (!q) {
        this.results = []
        this.loading = false
        return
      }
      this.loading = true
      this.debounceTimer = setTimeout(this.runSuggest, 220)
    },
    runSuggest() {
      const q = this.query.trim()
      if (!q) {
        this.results = []
        this.loading = false
        return
      }
      const seq = ++this.reqSeq
      this.loading = true
      searchget({ keyword: q, curr: 1, limit: 8 })
        .then((res) => {
          if (seq !== this.reqSeq) return // 过期响应，丢弃
          const list = (res && res.data && res.data.list) || []
          this.results = list
          this.loading = false
        })
        .catch(() => {
          if (seq !== this.reqSeq) return
          this.results = []
          this.loading = false
        })
    },
    clearQuery() {
      this.query = ""
      this.results = []
      this.activeIndex = -1
      this.focusInput()
    },

    /* ---------- 键盘导航 ---------- */
    move(dir) {
      const n = this.navCount
      if (!n) return
      let i = this.activeIndex + dir
      if (i < 0) i = n - 1
      if (i >= n) i = 0
      this.activeIndex = i
    },
    onEnter() {
      const n = this.navCount
      if (this.activeIndex >= 0 && this.activeIndex < n) {
        this.activateIndex(this.activeIndex)
      } else if (this.hasQuery) {
        this.fullSearch(this.query)
      }
    },
    activateIndex(i) {
      if (this.hasQuery) {
        if (i < this.results.length) this.goBook(this.results[i])
        else this.fullSearch(this.query)
      } else {
        if (i < this.history.length) this.fullSearch(this.history[i])
        else this.fullSearch(this.trending[i - this.history.length])
      }
    },

    /* ---------- 跳转 ---------- */
    goBook(book) {
      this.pushHistory(this.query.trim() || book.bookName)
      this.close()
      const id = book.id != null ? book.id : book.bookId
      this.$router.push({ name: "details", params: { id } }).catch(() => {})
    },
    fullSearch(term) {
      const t = (term || "").trim()
      if (!t) return
      this.pushHistory(t)
      this.query = t
      this.$store.commit("updateSearch", t)
      this.close()
      if (this.$route.path !== "/classify") {
        this.$router.push("/classify").catch(() => {})
      }
    },

    /* ---------- 历史记录 ---------- */
    loadHistory() {
      try {
        const raw = localStorage.getItem(HISTORY_KEY)
        this.history = raw ? JSON.parse(raw).slice(0, HISTORY_MAX) : []
      } catch (e) {
        this.history = []
      }
    },
    saveHistory() {
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(this.history))
      } catch (e) { /* 隐私模式等忽略 */ }
    },
    pushHistory(term) {
      const t = (term || "").trim()
      if (!t) return
      this.history = [t, ...this.history.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, HISTORY_MAX)
      this.saveHistory()
    },
    removeHistory(term) {
      this.history = this.history.filter((x) => x !== term)
      this.saveHistory()
    },
    clearHistory() {
      this.history = []
      this.saveHistory()
    },

    /* ---------- 热门 ---------- */
    loadTrending() {
      this.trendingLoaded = true
      indexClickRank()
        .then((res) => {
          const list = (res && res.data) || []
          const names = []
          for (const b of list) {
            const name = b && b.bookName
            if (name && !names.includes(name)) names.push(name)
            if (names.length >= 8) break
          }
          this.trending = names
        })
        .catch(() => {
          this.trendingLoaded = false // 允许下次重试
          this.trending = []
        })
    },

    /* ---------- 文字高亮 ---------- */
    highlightParts(text) {
      const name = text || ""
      const q = this.query.trim()
      if (!q) return [{ t: name, hit: false }]
      const idx = name.toLowerCase().indexOf(q.toLowerCase())
      if (idx < 0) return [{ t: name, hit: false }]
      return [
        { t: name.slice(0, idx), hit: false },
        { t: name.slice(idx, idx + q.length), hit: true },
        { t: name.slice(idx + q.length), hit: false },
      ].filter((p) => p.t.length)
    },

    // 点击空白处（面板/滚动区/外壳的空隙）收起 —— 仅移动端全屏态生效，桌面下拉沿用文档级外点关闭
    onSelfTap() {
      if (this.sheet) this.close()
    },
    // 面板离场动画结束：收起全屏布局并恢复祖先包含块（与内容淡出同步，无空白闪屏）
    onPanelLeft() {
      clearTimeout(this.leaveTimer)
      this.leaving = false
    },

    /* ---------- 移动端全屏：解除祖先包含块 ----------
       头部 .header 带 backdrop-filter，会成为 position:fixed 的包含块，
       导致全屏面板被「钉」在 ~48px 高的头部里、塌成一条 → 背后内容透出来。
       打开时临时把祖先链上的 backdrop-filter 置空（此刻面板已盖住头部，看不见），
       fixed 即回归视口、铺满全屏；关闭时原样还原。 */
    unclipFixed() {
      if (this._cbFix) return
      const fixes = []
      let el = this.$el && this.$el.parentElement
      while (el && el !== document.body && el !== document.documentElement) {
        const cs = window.getComputedStyle(el)
        const bf = cs.backdropFilter || cs.webkitBackdropFilter
        if (bf && bf !== "none") {
          fixes.push({ el, bf: el.style.backdropFilter, wbf: el.style.webkitBackdropFilter, tr: el.style.transition })
          // 关键：先禁掉过渡，让 backdrop-filter 同帧变 none。否则 .header 的
          // `transition: backdrop-filter .3s` 会让它渐变，期间 computed≠none → 包含块仍在、
          // 全屏 sheet 被困成一条，表现为「先出文字、~300ms 后才出毛玻璃底」的卡顿。
          el.style.transition = "none"
          el.style.backdropFilter = "none"
          el.style.webkitBackdropFilter = "none"
        }
        el = el.parentElement
      }
      this._cbFix = fixes
    },
    reclipFixed() {
      if (!this._cbFix) return
      this._cbFix.forEach(({ el, bf, wbf, tr }) => {
        // 先在「无过渡」状态下还原 backdrop-filter（同帧生效，不回放渐变），再恢复原 transition
        el.style.backdropFilter = bf || ""
        el.style.webkitBackdropFilter = wbf || ""
        el.style.transition = tr || ""
      })
      this._cbFix = null
    },

    /* ---------- 杂项 ---------- */
    lockScroll() {
      if (this.isMobile) document.body.style.overflow = "hidden"
    },
    unlockScroll() {
      document.body.style.overflow = ""
    },
    handleDocDown(e) {
      if (!this.open) return
      if (this.$el && !this.$el.contains(e.target)) this.close()
    },
    setupMql() {
      this.mql = window.matchMedia("(max-width: 768px)")
      this.isMobile = this.mql.matches
      this.mqlHandler = (e) => { this.isMobile = e.matches }
      if (this.mql.addEventListener) this.mql.addEventListener("change", this.mqlHandler)
      else this.mql.addListener(this.mqlHandler)
    },
  },
  created() {
    this.loadHistory()
  },
  mounted() {
    this.setupMql()
    this.docDownHandler = this.handleDocDown
    document.addEventListener("mousedown", this.docDownHandler, true)
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer)
    clearTimeout(this.leaveTimer)
    this.reclipFixed()
    this.unlockScroll()
    document.removeEventListener("mousedown", this.docDownHandler, true)
    if (this.mql) {
      if (this.mql.removeEventListener) this.mql.removeEventListener("change", this.mqlHandler)
      else this.mql.removeListener(this.mqlHandler)
    }
  },
}
</script>

<style lang="less">
.rn-search {
  /* ----- 设计 token ----- */
  --rn-accent: #008cd6;
  --rn-text: #1d1d1f;
  --rn-text2: #86868b;
  --rn-fill: rgba(118, 118, 128, 0.12);
  --rn-fill-strong: rgba(118, 118, 128, 0.2);
  --rn-hover: rgba(0, 0, 0, 0.05);
  --rn-card: #ffffff;
  --rn-line: rgba(0, 0, 0, 0.08);

  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;

  /* ===== 搜索条 ===== */
  .rn-search__bar {
    display: flex;
    align-items: center;
    width: 240px;
    height: 38px;
    box-sizing: border-box;
    padding: 0 10px;
    border-radius: 11px;
    background: var(--rn-fill);
    border: 1px solid transparent;
    cursor: text;
    transition: background 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, width 0.25s cubic-bezier(0.22, 1, 0.36, 1);

    .rn-search__icon {
      flex: 0 0 auto;
      width: 17px;
      height: 17px;
      color: var(--rn-text2);
      margin-right: 7px;
      transition: color 0.2s ease;
    }

    .rn-search__input {
      flex: 1 1 auto;
      min-width: 0;
      height: 100%;
      border: none;
      outline: none;
      background: transparent;
      font-size: 14px;
      color: var(--rn-text);
      caret-color: var(--rn-accent);

      &::placeholder { color: var(--rn-text2); }
    }

    .rn-search__clear {
      flex: 0 0 auto;
      display: inline-flex;
      padding: 0;
      border: none;
      background: none;
      cursor: pointer;
      color: var(--rn-text2);
      width: 18px;
      height: 18px;

      svg { width: 18px; height: 18px; }
      &:hover { color: var(--rn-text); }
    }
  }

  &.rn-search--open .rn-search__bar {
    background: var(--rn-card);
    border-color: var(--rn-line);
    box-shadow: 0 0 0 4px rgba(0, 140, 214, 0.14);
    .rn-search__icon { color: var(--rn-accent); }
  }

  /* ===== 浅色变体（深色 hero 之上，未聚焦时） ===== */
  &.rn-search--light:not(.rn-search--open) .rn-search__bar {
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    .rn-search__icon { color: rgba(255, 255, 255, 0.92); }
    .rn-search__input { color: #fff; &::placeholder { color: rgba(255, 255, 255, 0.78); } }
    .rn-search__clear { color: rgba(255, 255, 255, 0.85); }
  }

  /* ===== 联想面板 ===== */
  .rn-search__panel {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    width: 380px;
    max-width: 92vw;
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: saturate(180%) blur(24px);
    -webkit-backdrop-filter: saturate(180%) blur(24px);
    border: 0.5px solid var(--rn-line);
    border-radius: 16px;
    box-shadow: 0 16px 44px rgba(0, 0, 0, 0.16), 0 4px 14px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    z-index: 10000;
  }

  .rn-search__scroll {
    max-height: 70vh;
    overflow-y: auto;
    padding: 8px;
    -webkit-overflow-scrolling: touch;
  }

  /* 区块标题 */
  .rn-section__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px 6px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--rn-text2);

    .rn-section__action {
      border: none;
      background: none;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
      color: var(--rn-accent);
      padding: 2px 4px;
      &:hover { opacity: 0.75; }
    }
  }

  .rn-block + .rn-block { margin-top: 2px; }

  /* 结果行 */
  .rn-result {
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: none;
    background: none;
    border-radius: 11px;
    cursor: pointer;
    text-align: left;
    transition: background 0.12s ease;

    &.is-active { background: var(--rn-hover); }

    .rn-result__cover {
      flex: 0 0 auto;
      width: 38px;
      height: 52px;
      border-radius: 6px;
      overflow: hidden;
      margin-right: 12px;
      background: linear-gradient(135deg, #e9e9ee, #d9d9e0);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
      img { width: 100%; height: 100%; object-fit: cover; display: block; }
    }

    .rn-result__meta { flex: 1 1 auto; min-width: 0; }

    .rn-result__name {
      display: block;
      font-size: 14.5px;
      font-weight: 500;
      color: var(--rn-text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      .hit { color: var(--rn-accent); font-weight: 700; }
    }

    .rn-result__sub {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 3px;
      font-size: 12.5px;
      color: var(--rn-text2);
      .rn-result__author { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 170px; }
      .rn-result__score {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        flex: 0 0 auto;
        svg { width: 12px; height: 12px; color: #ff9f0a; }
      }
    }

    .rn-result__chev {
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      color: #c7c7cc;
      opacity: 0;
      transform: translateX(-4px);
      transition: opacity 0.15s ease, transform 0.15s ease;
    }
    &.is-active .rn-result__chev { opacity: 1; transform: translateX(0); }
  }

  /* 查看全部 */
  .rn-searchall {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    box-sizing: border-box;
    margin-top: 4px;
    padding: 11px 12px;
    border: none;
    border-top: 0.5px solid var(--rn-line);
    border-radius: 0 0 8px 8px;
    background: none;
    cursor: pointer;
    font-size: 13.5px;
    color: var(--rn-text);
    text-align: left;
    transition: background 0.12s ease;

    svg { width: 17px; height: 17px; color: var(--rn-accent); flex: 0 0 auto; }
    b { font-weight: 600; }
    span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    &.is-active { background: var(--rn-hover); }
  }

  /* 历史 chips */
  .rn-chips { display: flex; flex-wrap: wrap; gap: 8px; padding: 2px 6px 8px; }
  .rn-chip {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 6px 8px 6px 10px;
    border-radius: 980px;
    background: var(--rn-fill);
    cursor: pointer;
    font-size: 13px;
    color: var(--rn-text);
    transition: background 0.12s ease;

    &:hover, &.is-active { background: var(--rn-fill-strong); }
    .rn-chip__lead { width: 13px; height: 13px; margin-right: 5px; color: var(--rn-text2); flex: 0 0 auto; }
    .rn-chip__text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; }
    .rn-chip__del {
      display: inline-flex;
      align-items: center;
      margin-left: 4px;
      color: var(--rn-text2);
      svg { width: 14px; height: 14px; }
      &:hover { color: var(--rn-text); }
    }
  }

  /* 热门列表 */
  .rn-trend {
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    padding: 9px 10px;
    border: none;
    background: none;
    border-radius: 11px;
    cursor: pointer;
    text-align: left;
    transition: background 0.12s ease;

    &.is-active { background: var(--rn-hover); }
    .rn-trend__rank {
      flex: 0 0 auto;
      width: 20px;
      text-align: center;
      margin-right: 11px;
      font-size: 14px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
    .rn-trend__rank--hot { color: #ff453a; }
    .rn-trend__rank--cool { color: #c7c7cc; }
    .rn-trend__text {
      flex: 1 1 auto;
      min-width: 0;
      font-size: 14px;
      color: var(--rn-text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .rn-trend__flame { width: 14px; height: 14px; color: #ff6b2c; flex: 0 0 auto; margin-left: 8px; }
  }

  /* 加载骨架 */
  .rn-skeleton { padding: 4px 2px; }
  .rn-skeleton__row { display: flex; align-items: center; padding: 8px 10px; }
  .rn-skeleton__cover { width: 38px; height: 52px; border-radius: 6px; margin-right: 12px; flex: 0 0 auto; }
  .rn-skeleton__lines { flex: 1 1 auto; display: flex; flex-direction: column; gap: 8px; }
  .rn-skeleton__line { height: 11px; border-radius: 6px; }
  .rn-skeleton__line--w70 { width: 70%; }
  .rn-skeleton__line--w40 { width: 40%; }
  .rn-skeleton__cover,
  .rn-skeleton__line {
    background: linear-gradient(90deg, #ececf0 25%, #f5f5f7 37%, #ececf0 63%);
    background-size: 400% 100%;
    animation: rn-shimmer 1.3s ease infinite;
  }
  @keyframes rn-shimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }

  /* 空 / 提示 */
  .rn-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 30px 16px 26px;
    color: var(--rn-text2);
    svg { width: 30px; height: 30px; opacity: 0.6; }
    p { margin: 0; font-size: 13.5px; }
  }
  .rn-tip { padding: 22px 16px; text-align: center; font-size: 13px; color: var(--rn-text2); }

  /* 下拉动效 */
  .rn-pop-enter-active { transition: opacity 0.18s ease, transform 0.22s cubic-bezier(0.22, 1, 0.36, 1); }
  .rn-pop-leave-active { transition: opacity 0.14s ease, transform 0.14s ease; }
  .rn-pop-enter, .rn-pop-leave-to { opacity: 0; transform: translateY(-8px) scale(0.98); }

  .rn-search__cancel { display: none; }
}

/* ===================== 移动端：全屏搜索面板 ===================== */
@media (max-width: 768px) {
  /* 头部内的搜索条铺满中间区域 */
  .rn-search .rn-search__bar { width: 100%; height: 34px; }

  .rn-search.rn-search--sheet {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10001;
    /* 顶部输入框 + Cancel 同行，面板换行铺满下方 */
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    /* 与顶栏一致的苹果风毛玻璃（半透白 + 饱和模糊），背后页面被柔化而非直透 */
    background: rgba(255, 255, 255, 0.76);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    backdrop-filter: saturate(180%) blur(20px);
    padding: calc(env(safe-area-inset-top) + 8px) 12px 0;

    .rn-search__bar {
      width: auto;
      flex: 1 1 auto;
      height: 38px;
      background: #fff;
      border-color: var(--rn-line);
      box-shadow: none;
      align-self: center;
      order: 0;
    }

    .rn-search__cancel {
      display: inline-block;
      flex: 0 0 auto;
      align-self: center;
      order: 1;
      margin-left: 10px;
      border: none;
      background: none;
      color: var(--rn-accent);
      font-size: 16px;
      cursor: pointer;
      padding: 0 2px;
    }

    .rn-search__panel {
      order: 2;
      position: static;
      width: 100%;
      max-width: none;
      flex: 1 1 100%;
      margin-top: 10px;
      background: transparent;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      border: none;
      border-radius: 0;
      box-shadow: none;
    }

    .rn-search__scroll {
      max-height: none;
      height: calc(100vh - 60px - env(safe-area-inset-top));
      padding: 4px 0 24px;
    }

    /* 移动端面板用白卡分组承载内容 */
    .rn-result, .rn-trend { padding: 11px 14px; }
    .rn-section__title { padding: 12px 6px 8px; }
    .rn-result__chev { opacity: 1; transform: none; }
  }
}
</style>
